import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Links from '@/shared/constants/navegacao';
import { Link } from 'react-router-dom';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import type { ReadUsuarioDto } from '@/api.types';
import { GetAuditoriaEmails } from './auditoria-emails.service';
import type { AuditoriaEmailDto, FindAuditoriaEmailsDto } from './auditoria-emails.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

export default function LogsEmailsPage() {
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const [loaded, setLoaded] = useState(false);
  const [buscando, setBuscando] = useState(false);

  const [dataInicial, setDataInicial] = useState(primeiroDia);
  const [dataFinal, setDataFinal] = useState(ultimoDia);
  const [destinatario, setDestinatario] = useState('');
  const [acao, setAcao] = useState('');
  const [codigo, setCodigo] = useState('');
  const [idUsuario, setIdUsuario] = useState<number>(0);
  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);

  const [registros, setRegistros] = useState<[AuditoriaEmailDto[], number]>();

  const tableState = useDataTableState('enviadoEm');

  useEffect(() => {
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        const ordenados = result[0].sort((a, b) => a.username!.localeCompare(b.username!));
        setListaUsuarios(ordenados);
      })
      .catch((e) => toast.error(String(e)))
      .finally(() => setLoaded(true));
  }, []);

  const columns: Column<AuditoriaEmailDto>[] = useMemo(
    () => [
      {
        key: 'enviadoEm',
        header: 'Data',
        render: (row) => format(new Date(row.enviadoEm), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
      },
      {
        key: 'destinatarioEmail',
        header: 'Destinatário',
        render: (row) => <span className="font-monospace small">{row.destinatarioEmail}</span>,
      },
      { key: 'acao', header: 'Ação' },
      { key: 'codigo', header: 'Código' },
      {
        key: 'usuario.username',
        header: 'Remetente (usuário)',
        render: (row) => row.usuario?.username || 'Sistema',
      },
    ],
    [],
  );

  async function handleBuscar() {
    if (!loaded) return;

    setBuscando(true);
    try {
      const filters: FindAuditoriaEmailsDto = {
        skip: (tableState.currentPage - 1) * tableState.pageSize,
        take: tableState.pageSize,
        data_inicial: dataInicial.toISOString(),
        data_final: dataFinal.toISOString(),
        destinatario_email: destinatario.trim() || undefined,
        acao: acao.trim() || undefined,
        codigo: codigo.trim() || undefined,
        usuario_id: idUsuario || undefined,
      };

      const result = await GetAuditoriaEmails(filters);
      setRegistros(result);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBuscando(false);
    }
  }

  useEffect(() => {
    void handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, tableState.currentPage, tableState.pageSize, tableState.sortConfig]);

  return (
    <div className="row">
      <div className="col-12">
        <h3>Logs de E-mails</h3>
        <span className="blockquote-footer pb-4 text-end">
          Auditoria de envios de e-mail (tabela <code>auditoria_emails</code>).
        </span>
      </div>
      <hr />

      <div className="col-12 mb-3 d-flex gap-2">
        <Link to={Links.Auditorias_Index} className="btn btn-outline-primary btn-sm">
          Voltar para Auditorias
        </Link>
      </div>

      {registros && (
        <div className="col-12">
          <DataTable
            columns={columns}
            data={registros[0]}
            onChange={handleBuscar}
            serverSide={true}
            showFilter={false}
            states={tableState}
            totalRecords={registros[1]}
            renderFilters={() => (
              <div className="row">
                <div className="col-md-3">
                  <label htmlFor="usuario-email" className="col-form-label">
                    Usuário remetente:
                  </label>
                  <select
                    id="usuario-email"
                    className="form-select"
                    value={idUsuario}
                    onChange={(e) => setIdUsuario(Number(e.target.value))}
                  >
                    <option value={0}>Sistema</option>
                    {listaUsuarios.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label htmlFor="destinatario-email" className="col-form-label">
                    Destinatário contém:
                  </label>
                  <input
                    id="destinatario-email"
                    className="form-control font-monospace"
                    value={destinatario}
                    onChange={(e) => setDestinatario(e.target.value)}
                    placeholder="ex.: @defensoria.se.def.br"
                  />
                </div>

                <div className="col-md-3">
                  <label htmlFor="acao-email" className="col-form-label">
                    Ação contém:
                  </label>
                  <input
                    id="acao-email"
                    className="form-control"
                    value={acao}
                    onChange={(e) => setAcao(e.target.value)}
                    placeholder="ex.: Recuperar senha"
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="codigo-email" className="col-form-label">
                    Código:
                  </label>
                  <input
                    id="codigo-email"
                    className="form-control font-monospace"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    placeholder="SUCESSO / EAUTH / ..."
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="data-inicial-email" className="col-form-label">
                    Data inicial:
                  </label>
                  <input
                    id="data-inicial-email"
                    type="date"
                    className="form-control"
                    value={dataInicial.toISOString().slice(0, 10)}
                    onChange={(e) => setDataInicial(new Date(e.target.value))}
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="data-final-email" className="col-form-label">
                    Data final:
                  </label>
                  <input
                    id="data-final-email"
                    type="date"
                    className="form-control"
                    value={dataFinal.toISOString().slice(0, 10)}
                    onChange={(e) => setDataFinal(new Date(e.target.value))}
                  />
                </div>

                <div className="col-12 mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleBuscar}
                    disabled={buscando}
                  >
                    {buscando ? 'Buscando...' : 'Pesquisar'}
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}

